import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Button, Checkbox, Chip, Divider, Icon, IconButton, InputAdornment,
  Paper, TextField, Tooltip, Typography, Badge, useMediaQuery
} from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';

import ProgressBar from '../controls/ProgressBar';
import ErrorMessage from '../controls/ErrorMessage';
import NoActiveOrganisatieMessage from '../controls/NoActiveOrganisatieMessage';
import FilterCategorieen from './FilterCategorieen';

import { ListParams } from '../../models/listParams';
import { Notification } from '../../models/notification/notification';
import { OrganizationFilter } from '../../models/notification/organizationFilter';
import { UnreadCountByCategoryResponse } from '../../models/notification/unreadCount';
import { MijnOrganisatieListItem } from '../../models/user/mijnOrganisatieListItem';

import { notificationsService } from '../../services/notifications.service';
import { userManagementService } from '../../services/user-management.service';
import { authService } from '../../services/auth.service';
import { ApiError } from '../../services/utils/apiError';
import { LOCAL_STORAGE_KEYS } from '../../services/utils/localStorageKeys';
import { loadJsonFromLocalStorage, saveJsonToLocalStorage } from '../../services/utils/localStorageState';
import { dispatchNotificationsUpdated } from '../../../hooks/useUnreadNotificationsCount';


const PAGE_SIZE = 25;
const FORBIDDEN_MESSAGE = 'U heeft geen toegang tot dit onderdeel van infraplannen';
const notificationTypes = [
    'PlanSharingChanged', 
    'PlanBeoordelingChanged', 
    'PublicLayerAvailable', 
    'PortfolioAfstemmingsStatusChanged', 
    'OrganizationInvitedToPortfolio', 
    'PlanInvitedToPortfolio', 
    'TaskAssigned'
  ];

// Backend verwacht datums in het _filter zonder quotes en als ISO 8601 zonder milliseconden
// (bijv. 2026-05-26T09:38:52Z). Quotes of milliseconden leveren een 422-fout op.
const toFilterDate = (value: dayjs.Dayjs): string => value.toISOString().replace(/\.\d{3}Z$/, 'Z');
const linkIconButtonSx = {
  alignSelf: 'center',
  textDecoration: 'none !important',
  display: 'inline-flex !important',
  alignItems: 'center',
  justifyContent: 'center',
  width: 34,
  height: 34,
  p: 0.625,
  lineHeight: 1,
  verticalAlign: 'middle',
  '& .MuiIcon-root': {
    fontSize: 24,
    lineHeight: 1,
    display: 'block',
  },
} as const;

interface StoredBerichtencentrumFilterState {
  selectedSearch: string;
  selectedCategory: string | null;
  selectedSubCategory: string | null;
  selectedOrganizations: string[];
  onlyUnread: boolean;
  dateFrom: string | null;
  dateTo: string | null;
}

const getStoredBerichtencentrumFilterState = (): StoredBerichtencentrumFilterState => {
  const fallback: StoredBerichtencentrumFilterState = {
    selectedSearch: '',
    selectedCategory: null,
    selectedSubCategory: null,
    selectedOrganizations: [],
    onlyUnread: false,
    dateFrom: null,
    dateTo: null,
  };

  const hasNewKey = typeof window !== 'undefined' && localStorage.getItem(LOCAL_STORAGE_KEYS.BERICHTENCENTRUM_FILTERS) !== null;
  const storageKey = hasNewKey ? LOCAL_STORAGE_KEYS.BERICHTENCENTRUM_FILTERS : 'berichtencentrum.filters.v1';

  return loadJsonFromLocalStorage<StoredBerichtencentrumFilterState>(
    storageKey,
    fallback,
    (parsed) => ({
      selectedSearch: typeof parsed?.selectedSearch === 'string' ? parsed.selectedSearch : '',
      selectedCategory: typeof parsed?.selectedCategory === 'string' ? parsed.selectedCategory : null,
      selectedSubCategory: typeof parsed?.selectedSubCategory === 'string' ? parsed.selectedSubCategory : null,
      selectedOrganizations: Array.isArray(parsed?.selectedOrganizations) ? parsed.selectedOrganizations : [],
      onlyUnread: Boolean(parsed?.onlyUnread),
      dateFrom: typeof parsed?.dateFrom === 'string' ? parsed.dateFrom : null,
      dateTo: typeof parsed?.dateTo === 'string' ? parsed.dateTo : null,
    }),
  );
};

const Berichtencentrum: React.FC = () => {
  const initialFilterStateRef = useRef<StoredBerichtencentrumFilterState>(getStoredBerichtencentrumFilterState());

  // Vanaf 1200px en kleiner tonen we de bulk-acties als compacte icon buttons
  const compactActions = useMediaQuery('(max-width:1200px)');

  // Readonly-gebruikers mogen het Berichtencentrum bekijken en filteren, maar
  // geen berichten op gelezen/ongelezen zetten.
  const isReadOnly = authService.isReadOnly();
  
  const [mijnOrganisatie] = useState<MijnOrganisatieListItem | null>(() =>
    userManagementService.getStoredOrganisatie()
  );

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [searchInput, setSearchInput] = useState<string>(initialFilterStateRef.current.selectedSearch);
  const [debouncedSearch, setDebouncedSearch] = useState<string>(initialFilterStateRef.current.selectedSearch);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(initialFilterStateRef.current.selectedCategory);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(initialFilterStateRef.current.selectedSubCategory);
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>(initialFilterStateRef.current.selectedOrganizations);
  const [filterPanelOpen, setFilterPanelOpen] = useState<boolean>(true);

  const [onlyUnread, setOnlyUnread] = useState<boolean>(initialFilterStateRef.current.onlyUnread);
  const [dateFrom, setDateFrom] = useState<dayjs.Dayjs | null>(() => {
    const value = initialFilterStateRef.current.dateFrom;
    if (!value) return null;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  });
  const [dateTo, setDateTo] = useState<dayjs.Dayjs | null>(() => {
    const value = initialFilterStateRef.current.dateTo;
    if (!value) return null;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
  });

  const [categoriesData, setCategoriesData] = useState<UnreadCountByCategoryResponse | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationFilter[]>([]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set());

  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    saveJsonToLocalStorage(LOCAL_STORAGE_KEYS.BERICHTENCENTRUM_FILTERS, {
      selectedSearch: searchInput,
      selectedCategory,
      selectedSubCategory,
      selectedOrganizations,
      onlyUnread,
      dateFrom: dateFrom ? dateFrom.toISOString() : null,
      dateTo: dateTo ? dateTo.toISOString() : null,
    });
  }, [searchInput, selectedCategory, selectedSubCategory, selectedOrganizations, onlyUnread, dateFrom, dateTo]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const fetchingRef = useRef<boolean>(false);
  const reconcileTimer = useRef<NodeJS.Timeout | null>(null);

  const hasMore = notifications.length < totalCount;

  const buildFilter = useCallback((): string | undefined => {
    const clauses: string[] = [];
    if (selectedCategory) {
      clauses.push(`Category = '${selectedCategory}'`);
    }
    if (selectedSubCategory) {
      clauses.push(`SubCategory = '${selectedSubCategory}'`);
    }
    if (selectedOrganizations.length === 1) {
      clauses.push(`Actor.OrganizationId = '${selectedOrganizations[0]}'`);
    }
    else if (selectedOrganizations.length > 1) {
      clauses.push(`Actor.OrganizationId IN (${selectedOrganizations.map(o => `'${o}'`).join(', ')})`);
    }
    if (onlyUnread) {
      clauses.push(`IsRead = false`);
    }
    if (dateFrom) {
      clauses.push(`CreatedAtUtc >= ${toFilterDate(dateFrom.startOf('day'))}`);
    }
    if (dateTo) {
      clauses.push(`CreatedAtUtc <= ${toFilterDate(dateTo.endOf('day'))}`);
    }
    return clauses.length > 0 ? clauses.join(' AND ') : undefined;
  }, [selectedCategory, selectedSubCategory, selectedOrganizations, onlyUnread, dateFrom, dateTo]);

  const loadNotifications = useCallback(async (reset: boolean) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    if (reset) {
      setLoading(true);
    }
    else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      const offset = reset ? 0 : notifications.length;
      const params: ListParams = {
        _offset: offset,
        _limit: PAGE_SIZE,
        _sort: 'createdatutc desc',
        _search: debouncedSearch || undefined,
        _filter: buildFilter(),
      };

      const data = await notificationsService.getNotifications(params);
      const items = data.items ?? [];

      setTotalCount(data.count ?? 0);
      setNotifications(prev => (reset ? items : [...prev, ...items]));
    }
    catch (err: any) {
      if (err instanceof ApiError && err.isValidation()) {
        setError('De opgegeven filters zijn ongeldig. Pas het datumfilter aan en probeer het opnieuw.');
      }
      else {
        setError(err?.message || 'Er is een fout opgetreden bij het ophalen van de berichten.');
      }
    }
    finally {
      setLoading(false);
      setLoadingMore(false);
      fetchingRef.current = false;
    }
  }, [notifications.length, debouncedSearch, buildFilter]);

  const refreshCategoryCounts = useCallback(async () => {
    try {
      const [counts, orgs] = await Promise.all([
        notificationsService.getUnreadCountByCategory(),
        notificationsService.getOrganizationFilter(),
      ]);
      setCategoriesData(counts);
      setOrganizations(orgs ?? []);
    }
    catch {
      // Niet kritisch voor het tonen van de lijst
    }
  }, []);

  // Initiele load van categorie-tellers en organisatie-filter
  useEffect(() => {
    if (!mijnOrganisatie) return;
    refreshCategoryCounts();
  }, [mijnOrganisatie, refreshCategoryCounts]);

  // Reset + herlaad bij wijziging van filters/zoekterm
  useEffect(() => {
    if (!mijnOrganisatie) return;
    setSelectedIds(new Set());
    loadNotifications(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mijnOrganisatie, debouncedSearch, selectedCategory, selectedSubCategory, selectedOrganizations, onlyUnread, dateFrom, dateTo]);

  // Infinite scroll
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loading && !loadingMore && !fetchingRef.current) {
        loadNotifications(false);
      }
    }, { rootMargin: '200px' });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, loadNotifications]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 450);
  };

  const clearSearch = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setSearchInput('');
    setDebouncedSearch('');
  };

  useEffect(() => () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (reconcileTimer.current) clearTimeout(reconcileTimer.current);
  }, []);

  const handleSelectCategory = (category: string | null) => {
    setSelectedCategory(category);
    setSelectedSubCategory(null);
  };

  const handleClearAllFilters = () => {
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedOrganizations([]);
    setOnlyUnread(false);
    setDateFrom(null);
    setDateTo(null);
  };

  const updateLocalReadState = (ids: string[], isRead: boolean) => {
    const idSet = new Set(ids);
    setNotifications(prev => prev.map(n => (idSet.has(n.id) ? { ...n, isRead } : n)));
  };

  // Werk de ongelezen-tellers in het filterpaneel optimistisch bij, zodat ze
  // direct meebewegen met de leesstatus-wijziging. `isRead` bepaalt de richting.
  const applyUnreadDeltaToCategories = useCallback((changed: Notification[], isRead: boolean) => {
    if (changed.length === 0) return;
    const per = isRead ? -1 : 1;
    setCategoriesData(prev => {
      if (!prev) return prev;
      const categories = prev.categories.map(c => ({
        ...c,
        subCategories: c.subCategories.map(s => ({ ...s })),
      }));
      let totalDelta = 0;
      for (const n of changed) {
        const cat = categories.find(c => c.category === n.category);
        if (cat) {
          cat.unreadCount = Math.max(0, cat.unreadCount + per);
          const sub = cat.subCategories.find(s => s.subCategory === n.subCategory);
          if (sub) {
            sub.unreadCount = Math.max(0, sub.unreadCount + per);
          }
        }
        totalDelta += per;
      }
      return { ...prev, unreadCount: Math.max(0, prev.unreadCount + totalDelta), categories };
    });
  }, []);

  // Reconcilieer de filter-tellers kort na de wijziging met de server.
  const scheduleCategoryReconcile = useCallback(() => {
    if (reconcileTimer.current) clearTimeout(reconcileTimer.current);
    reconcileTimer.current = setTimeout(() => {
      refreshCategoryCounts();
    }, 2000);
  }, [refreshCategoryCounts]);

  const handleToggleRead = async (notification: Notification) => {
    const newIsRead = !notification.isRead;
    const delta = newIsRead ? -1 : 1;
    updateLocalReadState([notification.id], newIsRead);
    // Optimistisch de tellers in navigatie en filter bijwerken
    applyUnreadDeltaToCategories([notification], newIsRead);
    dispatchNotificationsUpdated(delta);
    try {
      await notificationsService.markRead([notification.id], newIsRead);
      scheduleCategoryReconcile();
    }
    catch (err: any) {
      // Terugdraaien bij fout
      updateLocalReadState([notification.id], notification.isRead);
      applyUnreadDeltaToCategories([notification], notification.isRead);
      dispatchNotificationsUpdated(-delta);
      if (err instanceof ApiError && err.isForbidden()) {
        setError(FORBIDDEN_MESSAGE);
      }
      else {
        setError(err?.message || 'Het bijwerken van de leesstatus is mislukt.');
      }
    }
  };

  const handleBulkMarkRead = async (isRead: boolean) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    // Alleen berichten die daadwerkelijk van status wijzigen tellen mee voor de tellers
    const changed = notifications.filter(n => selectedIds.has(n.id) && n.isRead !== isRead);
    const previous = notifications.filter(n => selectedIds.has(n.id)).map(n => ({ id: n.id, isRead: n.isRead }));

    updateLocalReadState(ids, isRead);
    // Optimistisch de tellers in navigatie en filter bijwerken
    applyUnreadDeltaToCategories(changed, isRead);
    dispatchNotificationsUpdated(changed.length * (isRead ? -1 : 1));

    try {
      // De API verwerkt maximaal 100 berichten per aanroep
      for (let i = 0; i < ids.length; i += 100) {
        await notificationsService.markRead(ids.slice(i, i + 100), isRead);
      }
      scheduleCategoryReconcile();
      setSelectedIds(new Set());
    }
    catch (err: any) {
      previous.forEach(p => updateLocalReadState([p.id], p.isRead));
      applyUnreadDeltaToCategories(changed, !isRead);
      dispatchNotificationsUpdated(changed.length * (isRead ? 1 : -1));
      if (err instanceof ApiError && err.isForbidden()) {
        setError(FORBIDDEN_MESSAGE);
      }
      else {
        setError(err?.message || 'Het bijwerken van de leesstatus is mislukt.');
      }
    }
  };

  const allVisibleSelected = notifications.length > 0 && selectedIds.size === notifications.length;
  const someVisibleSelected = selectedIds.size > 0 && selectedIds.size < notifications.length;

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
    }
    else {
      setSelectedIds(new Set(notifications.map(n => n.id)));
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      }
      else {
        next.add(id);
      }
      return next;
    });
  };

  const formatDate = useMemo(() => (value: string) => {
    const date = dayjs(value);
    return date.isValid() ? date.locale('nl').format('D MMMM YYYY HH:mm') : value;
  }, []);

  // Groepeer de berichten per dag. De lijst is al gesorteerd op datum (aflopend),
  // dus de dagen volgen automatisch in de juiste volgorde.
  const groupedByDay = useMemo(() => {
    const today = dayjs().startOf('day');
    const yesterday = today.subtract(1, 'day');
    const groups: { key: string; label: string; items: Notification[] }[] = [];
    const index = new Map<string, { key: string; label: string; items: Notification[] }>();

    for (const notification of notifications) {
      const date = dayjs(notification.createdAtUtc);
      const key = date.isValid() ? date.format('YYYY-MM-DD') : 'onbekend';
      let group = index.get(key);
      if (!group) {
        let label: string;
        if (!date.isValid()) {
          label = 'Onbekende datum';
        }
        else if (date.startOf('day').isSame(today)) {
          label = 'Vandaag';
        }
        else if (date.startOf('day').isSame(yesterday)) {
          label = 'Gisteren';
        }
        else {
          const formatted = date.locale('nl').format('dddd D MMMM YYYY');
          label = formatted.charAt(0).toUpperCase() + formatted.slice(1);
        }
        group = { key, label, items: [] };
        index.set(key, group);
        groups.push(group);
      }
      group.items.push(notification);
    }

    return groups;
  }, [notifications]);

  const toggleDayCollapse = (key: string) => {
    setCollapsedDays(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      }
      else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleSelectDay = (items: Notification[]) => {
    const ids = items.map(item => item.id);
    const allSelected = ids.length > 0 && ids.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        ids.forEach(id => next.delete(id));
      }
      else {
        ids.forEach(id => next.add(id));
      }
      return next;
    });
  };

  if (!mijnOrganisatie) {
    return (
      <Paper>
        <NoActiveOrganisatieMessage />
      </Paper>
    );
  }

  const activeFilterCount =
    (selectedCategory ? 1 : 0) +
    (selectedOrganizations.length > 0 ? 1 : 0) +
    (onlyUnread ? 1 : 0) +
    (dateFrom || dateTo ? 1 : 0);

  return (
    <div className="berichtencentrum portfolio-with-filter-panel">
      <div className={`filter-panel-left${filterPanelOpen ? '' : ' filter-panel-left--closed'}`}>
        <FilterCategorieen
          categoriesData={categoriesData}
          selectedCategory={selectedCategory}
          selectedSubCategory={selectedSubCategory}
          organizations={organizations}
          selectedOrganizations={selectedOrganizations}
          onlyUnread={onlyUnread}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onSelectCategory={handleSelectCategory}
          onSelectSubCategory={setSelectedSubCategory}
          onChangeOrganizations={setSelectedOrganizations}
          onChangeOnlyUnread={setOnlyUnread}
          onChangeDateFrom={setDateFrom}
          onChangeDateTo={setDateTo}
          onClearAll={handleClearAllFilters}
          onClose={() => setFilterPanelOpen(false)}
        />
      </div>

      <div className="portfolio-content-area">
        <div className="sidebar-search-bar">
          <TextField
            label="Zoeken in berichten"
            variant="filled"
            placeholder="Zoeken op tekst in de notificatie"
            fullWidth
            value={searchInput}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch} sx={{ p: 0.5 }}>
                    <Icon fontSize="small">clear</Icon>
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Badge
            badgeContent={activeFilterCount}
            color="primary"
            overlap="rectangular"
            sx={{ '& .MuiBadge-badge': { top: 6, right: 6 } }}
          >
            <Tooltip title={filterPanelOpen ? 'Sluit het filter' : 'Open filter om uitgebreid te zoeken in berichten'}>
              <IconButton size="large" onClick={() => setFilterPanelOpen(!filterPanelOpen)}>
                <Icon>{filterPanelOpen ? 'filter_list_off' : 'filter_list'}</Icon>
              </IconButton>
            </Tooltip>
          </Badge>
        </div>

        <Paper sx={{ p: 1, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {!isReadOnly && (
                <Tooltip title="Selecteer alle zichtbare berichten">
                  <Checkbox
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onChange={toggleSelectAll}
                    disabled={notifications.length === 0}
                    sx={{ ml: '8px' }}
                  />
                </Tooltip>
              )}
              <Typography variant="body2" color="text.secondary">
                {selectedIds.size > 0 ? `${selectedIds.size} geselecteerd` : `${totalCount} berichten`}
              </Typography>
            </Box>

            <div className="btn-group">
              {isReadOnly ? null : compactActions ? (
                <>
                  <Tooltip title="Markeer geselecteerde berichten als gelezen" placement="top" arrow>
                    <span>
                      <IconButton
                        color="secondary"
                        disabled={selectedIds.size === 0}
                        onClick={() => handleBulkMarkRead(true)}
                        aria-label="Markeer als gelezen"
                      >
                        <Icon>drafts</Icon>
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Markeer geselecteerde berichten als ongelezen" placement="top" arrow>
                    <span>
                      <IconButton
                        color="secondary"
                        disabled={selectedIds.size === 0}
                        onClick={() => handleBulkMarkRead(false)}
                        aria-label="Markeer als ongelezen"
                      >
                        <Icon>mark_email_unread</Icon>
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip title="Markeer geselecteerde berichten als gelezen" placement="top" arrow>
                    <Button
                      variant="text"
                      color="secondary"
                      disabled={selectedIds.size === 0}
                      onClick={() => handleBulkMarkRead(true)}
                    >
                      <Icon>drafts</Icon>
                      Markeer als gelezen
                    </Button>
                  </Tooltip>
                  <Tooltip title="Markeer geselecteerde berichten als ongelezen" placement="top" arrow>
                    <Button
                      variant="text"
                      color="secondary"
                      disabled={selectedIds.size === 0}
                      onClick={() => handleBulkMarkRead(false)}
                    >
                      <Icon>mark_email_unread</Icon>
                      Markeer als ongelezen
                    </Button>
                  </Tooltip>
                </>
              )}
            </div>
          </Box>

          <Divider sx={{ mt: 1 }} />

          {!loading && notifications.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">Er zijn geen berichten.</Typography>
            </Box>
          )}

          <Box>
            {groupedByDay.map((group) => {
              const dayIds = group.items.map(item => item.id);
              const dayAllSelected = dayIds.length > 0 && dayIds.every(id => selectedIds.has(id));
              const daySomeSelected = dayIds.some(id => selectedIds.has(id)) && !dayAllSelected;
              const dayCollapsed = collapsedDays.has(group.key);

              return (
                <Box key={group.key} className="bericht-dag-groep">
                  <Box
                    className="bericht-dag-header"
                    onClick={() => toggleDayCollapse(group.key)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.5,
                      cursor: 'pointer',
                      backgroundColor: 'action.hover',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <Tooltip title="Selecteer alle berichten van deze dag">
                      <Checkbox
                        checked={dayAllSelected}
                        indeterminate={daySomeSelected}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => toggleSelectDay(group.items)}
                        sx={{ ml: '8px', display: isReadOnly ? 'none' : undefined }}
                      />
                    </Tooltip>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {group.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({group.items.length})
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title={dayCollapsed ? 'Uitklappen' : 'Inklappen'}>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleDayCollapse(group.key);
                        }}
                        aria-label={dayCollapsed ? 'Uitklappen' : 'Inklappen'}
                      >
                        <Icon>{dayCollapsed ? 'expand_more' : 'expand_less'}</Icon>
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {!dayCollapsed && group.items.map((notification) => {
                    const isSelected = selectedIds.has(notification.id);
              const organisatieNaam = notification.actor?.organizationName;
              const afstemmingsdossierId = notification.context?.portfolio?.id;
              const afstemmingsdossierNaam = notification.context?.portfolio?.name ?? undefined;
              const planId = notification.context?.infpl?.id;
              const planNaam = notification.context?.infpl?.name ?? undefined;
              const isPublicLayer = notification.type === 'PublicLayerAvailable';
              const layerId = notification.context?.layer?.id;
              const layerDisplayName = notification.context?.layer?.name ?? undefined;
              const canManageLayerVisibility = authService.isKeyUser();
              const layerTooltip = layerDisplayName
                ? `Maak de kaartlaag ${layerDisplayName} zichtbaar voor je organisatie`
                : 'Maak de kaartlaag zichtbaar voor je organisatie';
              const afstemmingsdossierTooltip = afstemmingsdossierNaam
                ? `Ga naar het afstemmingsdossier ${afstemmingsdossierNaam}`
                : 'Ga naar het afstemmingsdossier';
              const planTooltip = planNaam
                ? `Ga naar het plan ${planNaam}`
                : 'Ga naar het plan';

              return (
                <Box
                  key={notification.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    p: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    borderLeft: '5px solid',
                    borderLeftColor: notification.isRead ? 'transparent' : '#ed6c02',
                    backgroundColor: notification.isRead ? 'transparent' : '#fff0d9',
                  }}
                  className={`bericht ${notification.isRead ? 'bericht--gelezen' : 'bericht--ongelezen'}`}
                >
                  {!isReadOnly && (
                    <Checkbox
                      checked={isSelected}
                      onChange={() => toggleSelectOne(notification.id)}
                      sx={{ alignSelf: 'center' }}
                    />
                  )}

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: notification.isRead ? 400 : 600, whiteSpace: 'pre-wrap' }}
                    >
                      {notification.message}
                    </Typography>

                    {notification.additionalInfo && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}
                      >
                        {notification.additionalInfo}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 0.75 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'flex-start' }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(notification.createdAtUtc)}
                        </Typography>
                        {organisatieNaam && (
                          <Typography variant="caption" color="text.secondary">
                            {organisatieNaam}
                          </Typography>
                        )}
                        
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={notification.subCategory
                            ? `${notification.category} > ${notification.subCategory}`
                            : notification.category}
                        />

                        {afstemmingsdossierId && (
                          <Tooltip title={afstemmingsdossierTooltip} placement="top" arrow>
                            <IconButton
                              component={Link}
                              color="secondary"
                              size="small"
                              to={`/dossiers/${afstemmingsdossierId}`}
                              sx={linkIconButtonSx}
                              aria-label={afstemmingsdossierTooltip}
                            >
                              <Icon>work</Icon>
                            </IconButton>
                          </Tooltip>
                        )}
                        {planId && (
                          <Tooltip title={planTooltip} placement="top" arrow>
                            <IconButton
                              component={Link}
                              color="secondary"
                              size="small"
                              to={`/plannen/${planId}`}
                              sx={linkIconButtonSx}
                              aria-label={planTooltip}
                            >
                              <Icon>map</Icon>
                            </IconButton>
                          </Tooltip>
                        )}
                        {isPublicLayer && layerId && canManageLayerVisibility && (
                          <Tooltip title={layerTooltip} placement="top" arrow>
                            <IconButton
                              component={Link}
                              color="secondary"
                              size="small"
                              to={`/mijn/kaartlagen/zichtbaarheid?zoek=${encodeURIComponent(layerDisplayName ?? '')}`}
                              sx={linkIconButtonSx}
                              aria-label="Maak de kaartlaag zichtbaar voor je organisatie"
                            >
                              <Icon>visibility</Icon>
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                        {/* <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={notification.subCategory
                            ? `${notification.category} > ${notification.subCategory}`
                            : notification.category}
                        /> */}
                      </Box>
                    </Box>
                  </Box>

                  {!isReadOnly && (
                    <Tooltip title={notification.isRead ? 'Markeer als ongelezen' : 'Markeer als gelezen'}>
                      <IconButton onClick={() => handleToggleRead(notification)} sx={{ alignSelf: 'center' }}>
                        <Icon color={notification.isRead ? 'inherit' : 'warning'}>
                          {notification.isRead ? 'drafts' : 'mark_email_unread'}
                        </Icon>
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                  );
                  })}
                </Box>
              );
            })}
          </Box>

          <div ref={sentinelRef} style={{ height: 1 }} />
          {loadingMore && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">Meer berichten laden&hellip;</Typography>
            </Box>
          )}
        </Paper>

        {loading && <ProgressBar />}
        {error && (
          <ErrorMessage
            message={error}
            autoHideDuration={6000}
            onClose={() => setError(null)}
          />
        )}
      </div>
    </div>
  );
};

export default Berichtencentrum;
